import {DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER, entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useMutation} from '@github-ui/react-query'
import {rejectPendingIssueSuggestionsMutation as queryId} from '@github-ui/server-persisted-queries'
import type {RejectPendingIssueSuggestionsMutationMutation} from '@github-ui/server-persisted-queries/types/rejectPendingIssueSuggestionsMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import {
  type PendingSuggestionMutationOptions,
  pendingSuggestionOperation,
  type SuggestionRef,
  toSuggestionInput,
} from '../utils/suggestion-ref'
import {issueLifecyclePath} from './issue-lifecycle-mutation'

export type RejectSuggestionRef = SuggestionRef
type RejectPendingIssueSuggestionsVariables = {
  operation: ReturnType<typeof pendingSuggestionOperation>
  actorId: string
  suggestions: SuggestionRef[]
  onRejected?: () => void
  onRollback?: () => void
}

export function useRejectPendingIssueSuggestions({
  issueId,
  owner,
  repository,
  issueNumber,
  onCompleted,
  onError,
}: PendingSuggestionMutationOptions<RejectPendingIssueSuggestionsMutationMutation>) {
  const {mutate} = useMutation<
    RejectPendingIssueSuggestionsMutationMutation,
    Error,
    RejectPendingIssueSuggestionsVariables
  >({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: async ({actorId, suggestions}) => {
      const path = `${issueLifecyclePath(owner, repository, issueNumber)}/pending_suggestions.json`
      const response = await reactFetchJSON(`${path}?${new URLSearchParams({queryId})}`, {
        method: 'DELETE',
        body: {actorId, suggestions: suggestions.map(toSuggestionInput)},
      })
      if (!response.ok) throw new Error(`Could not decline suggestions (${response.status})`)
      const json = (await response.json()) as {
        data?: RejectPendingIssueSuggestionsMutationMutation
        errors?: Array<{message?: string}>
      }
      // Preserve Relay's distinction between GraphQL errors with data and a failed request.
      if (!json.data?.rejectPendingIssueSuggestions) {
        throw new Error(json.errors?.[0]?.message ?? 'Could not decline suggestions')
      }
      return json.data
    },
    onMutate: ({onRejected}) => onRejected?.(),
    onSuccess: async (data, {operation}) => {
      await notifySettlement(operation, ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH)
      onCompleted?.(data)
    },
    onError: (error, {onRollback}) => {
      onRollback?.()
      onError?.(error)
    },
  })

  return useCallback(
    (actorId: string, suggestions: SuggestionRef[], onRejected?: () => void, onRollback?: () => void) => {
      const operation = pendingSuggestionOperation(issueId, {
        suggestionIds: suggestions.map(suggestion => suggestion.id),
        state: 'REJECTED',
      })
      mutate({operation, actorId, suggestions, onRejected, onRollback})
    },
    [issueId, mutate],
  )
}
